import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MentorPlansCard from "../components/MentorPlansCard";
import StudentAccessCard from "../components/StudentAccessCard";
import { ChangePasswordCard } from "../components/ChangePasswordCard";
import { PushSettingsCard } from "../components/PushSettingsCard";
import {
  Award,
  Zap,
  Flame,
  Crown,
} from "lucide-react";

export default function ProfilePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await api.get("/api/operations/dashboard");
      setData(res.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Profile error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchProfile();
  }, [user, fetchProfile]);

  if (loading && !data) {
    return <div className="p-8 text-center text-gray-400">Carregando perfil...</div>;
  }

  const op = data?.operation || {};
  const xp = op?.xp || 100;
  const levelNum = op?.level || 1;
  const levelName = op?.level_name || "Recruta";
  const xpCurr = op?.xp_curr || 0;
  const xpNext = op?.xp_next || 300;
  const xpProgress = Math.min(100, Math.round((xpCurr / xpNext) * 100));

  const isPlatformOwner = user?.role === "platform_owner";
  const isMentor = user?.role === "admin";
  const isStudent = !isPlatformOwner && !isMentor;

  const achievements = [
    { id: "primeira_missao", title: "Primeira Missão", desc: "Completou o 1º passo na Caverna", icon: "🏅", unlocked: true },
    { id: "sai_da_caverna", title: "Saí da Caverna", desc: "Desbloqueou o Modo Live", icon: "🕳️", unlocked: op?.mode === "MODO_LIVE" },
    { id: "cientista", title: "Cientista", desc: "Completou o Pré-Teste com 3 leads em Valores", icon: "🧪", unlocked: op?.stage !== "CAVE_PROFILE" && op?.stage !== "LIVE_PRETEST" },
    { id: "primeira_venda", title: "Primeira Venda", desc: "Registrou a primeira venda real", icon: "💰", unlocked: (op?.total_sales_count || 0) > 0 },
    { id: "hat_trick", title: "Hat Trick", desc: "Vendeu por 3 dias seguidos", icon: "🔥", unlocked: (op?.sales_streak_days || 0) >= 3 },
    { id: "escalador", title: "Escalador", desc: "Atingiu a fase de Escala", icon: "🚀", unlocked: op?.stage === "LIVE_SCALE" },
    { id: "operacao_forte", title: "Operação Forte", desc: "Alcançou Saúde da Operação >90%", icon: "💎", unlocked: (op?.health_score || 0) >= 90 },
  ];

  return (
    <div data-testid="profile-page-container" className="space-y-6 max-w-4xl mx-auto px-4 py-4 pb-20">
      {/* Gamified profile header — visible to everyone */}
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF6B00] to-[#E05000] flex items-center justify-center font-extrabold text-black text-xl shadow-[0_0_20px_rgba(255,107,0,0.4)]">
            {user?.name?.charAt(0) || "A"}
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-white">{user?.name}</h1>
            <div className="text-xs text-gray-400">{user?.email}</div>
            <div className="flex items-center gap-2 mt-1 text-xs flex-wrap">
              {isPlatformOwner && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-black bg-[#FF6B00] px-2 py-0.5 rounded uppercase tracking-wider">
                  <Crown className="w-3 h-3" /> Dono da Plataforma
                </span>
              )}
              {isMentor && (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold text-[#FF6B00] border border-[#FF6B00]/50 px-2 py-0.5 rounded uppercase tracking-wider">
                  Mentor
                </span>
              )}
              {isStudent && (
                <>
                  <span className="font-bold text-[#FF6B00]">Nível {levelNum} — {levelName}</span>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-300 font-mono">{xp} XP Total</span>
                </>
              )}
            </div>
          </div>
        </div>

        {isStudent && (
          <div className="flex gap-2 text-xs">
            <div className="bg-[#1A1D26] border border-[#282D3D] px-3 py-2 rounded-lg text-center">
              <span className="text-gray-400 text-[10px] block">Streak Operacional</span>
              <span className="text-sm font-extrabold text-white flex items-center justify-center gap-1">
                <Flame className="w-4 h-4 text-orange-500 fill-orange-500" />
                {op?.operational_streak_days || 1} dias
              </span>
            </div>
            <div className="bg-[#1A1D26] border border-[#282D3D] px-3 py-2 rounded-lg text-center">
              <span className="text-gray-400 text-[10px] block">Streak de Vendas</span>
              <span className="text-sm font-extrabold text-emerald-400 flex items-center justify-center gap-1">
                <Zap className="w-4 h-4 text-emerald-400 fill-emerald-400" />
                {op?.sales_streak_days || 0} dias
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Role-aware access section */}
      {isPlatformOwner && (
        <div data-testid="owner-access-card" className="bg-[#14161C] border border-[#FF6B00]/40 rounded-xl p-5 shadow-[0_0_18px_rgba(255,107,0,0.15)]">
          <div className="flex items-center gap-2 mb-2">
            <Crown className="w-5 h-5 text-[#FF6B00]" />
            <h3 className="text-base font-bold text-white">Acesso Total — Dono da Plataforma</h3>
          </div>
          <p className="text-xs text-gray-300 leading-relaxed">
            Sua conta possui acesso irrestrito a todas as mentorias, mentores e planos.
            Não há plano associado, cobrança ou limite de capacidade.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3">
              <span className="text-[10px] text-gray-400 block">Escopo</span>
              <span className="font-bold text-white">Toda a plataforma</span>
            </div>
            <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3">
              <span className="text-[10px] text-gray-400 block">Cobrança</span>
              <span className="font-bold text-white">Isento</span>
            </div>
            <div className="bg-[#1A1D26] border border-[#282D3D] rounded-lg p-3">
              <span className="text-[10px] text-gray-400 block">Capacidade</span>
              <span className="font-bold text-white">Ilimitada</span>
            </div>
          </div>
        </div>
      )}

      {isMentor && <MentorPlansCard />}

      {isStudent && (
        <>
          <StudentAccessCard />

          {/* Level & XP Progress Bar */}
          <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5">
            <div className="flex justify-between items-center text-xs font-semibold mb-2">
              <span className="text-gray-300">Progresso para o Próximo Nível</span>
              <span className="text-[#FF6B00] font-bold">
                {xpCurr} / {xpNext} XP para Nível {levelNum + 1}
              </span>
            </div>
            <div className="w-full h-3 bg-[#1A1D26] rounded-full overflow-hidden border border-[#282D3D]">
              <div
                className="h-full bg-gradient-to-r from-yellow-500 to-[#FF6B00] rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(255,107,0,0.4)]"
                style={{ width: `${xpProgress}%` }}
              />
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5">
            <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-[#FF6B00]" />
              <span>Conquistas & Medalhas da Operação</span>
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {achievements.map((ach) => (
                <div
                  key={ach.id}
                  className={`p-3 rounded-xl border text-center transition-all ${
                    ach.unlocked
                      ? "bg-[#1A1D26] border-[#FF6B00]/40 shadow-[0_0_12px_rgba(255,107,0,0.15)]"
                      : "bg-[#111318] border-[#222736] opacity-40 grayscale"
                  }`}
                >
                  <div className="text-2xl mb-1">{ach.icon}</div>
                  <div className="text-xs font-bold text-white">{ach.title}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5 line-clamp-2">{ach.desc}</div>
                  {ach.unlocked && (
                    <span className="text-[9px] text-emerald-400 font-bold mt-1 block">Conquistada</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <PushSettingsCard />
      <ChangePasswordCard />
    </div>
  );
}
